import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import {
  fromSeoUrl,
  toSeoUrl,
  toSingular,
  toPlural,
} from 'helpers/HelperFunctions';
import toSentenceCase from 'helpers/sentenceCase';
import {
  MAX_URL_PAGE,
  buildJobsBatchUrl,
  clampJobsPage,
} from '../jobsPagination';
import getJobsCanonical, {
  jobsListingHasFilterQuery,
} from '../getJobsCanonical';
import buildJobsListingCopy from '../buildJobsListingCopy';

import styles from './ProjectsHeader.module.scss';

const ProjectsHeader = ({
  filter = null,
  getSelectedRoleName,
  config,
  loggedIn,
  liveJobCount = 0,
  batchPage = 1,
  moreToLoad = false,
  locationPathname = '',
  locationSearch = '',
  onJobsBreadcrumbClick = null,
  children = null,
}) => {
  const selectedRoleName = getSelectedRoleName() || '';
  const roleSlug =
    (filter && filter.name) ||
    (selectedRoleName ? toSeoUrl(toPlural(selectedRoleName)) : null);
  const country = (filter && filter.country) || null;
  const city = (filter && filter.city) || null;

  const copy = buildJobsListingCopy({
    role: roleSlug,
    roleDisplayName: selectedRoleName || null,
    country,
    city,
    liveJobCount,
    isLoggedIn: Boolean(loggedIn),
  });

  const baseUrl = (config.baseUrl || '').replace(/\/$/, '');
  const { canonical } = getJobsCanonical({
    role: (filter && filter.name) || null,
    country,
    city,
    query: locationSearch,
    baseUrl,
  });

  const pathname =
    locationPathname || canonical.replace(baseUrl, '') || '/jobs';
  const absoluteBatchUrl = (page) =>
    `${baseUrl}${buildJobsBatchUrl(pathname, locationSearch, page)}`;

  // SEO prev/next use the crawlable URL page (capped at MAX_URL_PAGE)
  const urlBatchPage = clampJobsPage(batchPage);

  const helmetLinks = [
    {
      rel: 'canonical',
      href: canonical,
    },
  ];

  if (urlBatchPage > 1) {
    helmetLinks.push({
      rel: 'prev',
      href: absoluteBatchUrl(urlBatchPage - 1),
    });
  }

  if (moreToLoad && urlBatchPage < MAX_URL_PAGE) {
    helmetLinks.push({
      rel: 'next',
      href: absoluteBatchUrl(urlBatchPage + 1),
    });
  }

  const helmetMeta = [
    { name: 'description', content: copy.metaDescription },
    { property: 'og:title', content: copy.title },
    { property: 'og:description', content: copy.metaDescription },
    {
      property: 'og:url',
      content: canonical,
    },
    { name: 'twitter:title', content: copy.title },
    { name: 'twitter:description', content: copy.metaDescription },
  ];

  // Query filter variants (remote, experience, multi-role, etc.) — not clean
  // role / country / city / role+location path pages.
  if (jobsListingHasFilterQuery(locationSearch)) {
    helmetMeta.push({ name: 'robots', content: 'noindex' });
  }

  const roleLabelRaw = selectedRoleName
    ? toSingular(selectedRoleName)
    : filter && filter.name
    ? toSingular(fromSeoUrl(filter.name))
    : '';
  const roleLabel = roleLabelRaw ? toSentenceCase(roleLabelRaw) : '';
  const countryLabel = country ? fromSeoUrl(country) : '';
  const locationString = city
    ? ` in ${fromSeoUrl(city)}, ${fromSeoUrl(country)}`
    : country
    ? ` in ${fromSeoUrl(country)}`
    : '';

  // Guests/crawlers only — logged-in header stays compact (H1 only).
  const showBreadcrumb = !loggedIn && Boolean(roleLabel || countryLabel);

  const titleClassName = loggedIn
    ? `${styles.title} ${styles.titleLoggedIn}`
    : styles.title;

  const handleJobsBreadcrumbClick = (event) => {
    if (!onJobsBreadcrumbClick) {
      return;
    }
    // Let modified / non-primary clicks follow the link (new tab, etc.).
    if (
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      (typeof event.button === 'number' && event.button !== 0)
    ) {
      return;
    }
    event.preventDefault();
    onJobsBreadcrumbClick();
  };

  return (
    <div>
      <Helmet title={copy.title} link={helmetLinks} meta={helmetMeta} />

      {showBreadcrumb && (
        <Breadcrumbs
          className={styles.breadcrumbs}
          data-testid="jobs-breadcrumb"
        >
          <Link to="/jobs" onClick={handleJobsBreadcrumbClick}>
            Jobs
          </Link>
          {roleLabel ? (
            <span>{`${roleLabel} jobs`}</span>
          ) : (
            <span>{`Jobs${locationString}`}</span>
          )}
          {roleLabel && countryLabel ? <span>{countryLabel}</span> : null}
        </Breadcrumbs>
      )}

      <h1 className={titleClassName} data-testid="jobs-listing-h1">
        {copy.h1}
      </h1>
      {!loggedIn && copy.intro ? (
        <p className={styles.description}>{copy.intro}</p>
      ) : null}
      {children}
    </div>
  );
};

ProjectsHeader.propTypes = {
  filter: PropTypes.object,
  getSelectedRoleName: PropTypes.func,
  config: PropTypes.object,
  loggedIn: PropTypes.bool,
  liveJobCount: PropTypes.number,
  batchPage: PropTypes.number,
  moreToLoad: PropTypes.bool,
  locationPathname: PropTypes.string,
  locationSearch: PropTypes.string,
  onJobsBreadcrumbClick: PropTypes.func,
  children: PropTypes.node,
};

ProjectsHeader.defaultProps = {
  filter: null,
  liveJobCount: 0,
  batchPage: 1,
  moreToLoad: false,
  locationPathname: '',
  locationSearch: '',
  onJobsBreadcrumbClick: null,
  children: null,
};

export default ProjectsHeader;
